import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule } from '@porsche-design-system/components-angular';
import {
  combineLatest,
  defer,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
  type Observable,
} from 'rxjs';
import { UserIconComponent } from '../../shared/components/user-icon/user-icon.component';
import {
  FunTransactionLogType,
  type FunTransactionLogForMonth,
  type FunTransactionLogForYear,
  type FunTransactionLogRecord,
} from '../../shared/models/api/fun-transaction-log.model';
import type { Group } from '../../shared/models/api/group.model';
import type { User } from '../../shared/models/api/user.model';
import { OpaCurrencyPipe } from '../../shared/pipes/opa-currency.pipe';
import { OrderByPipe } from '../../shared/pipes/order-by.pipe';
import { GroupService } from '../../shared/services/api/group.service';
import { UserService } from '../../shared/services/api/user.service';
import { transactionLogTypeTranslation } from './fun-budget.helpers';
import type { TransactionLogData } from './fun-budget.models';
import { GroupMembersDirective } from './group-members.directive';

@Component({
  selector: 'opa-fun-budget',
  imports: [
    CommonModule,
    OpaCurrencyPipe,
    PorscheDesignSystemModule,
    RouterLink,
    UserIconComponent,
    LetDirective,
    OrderByPipe,
    ReactiveFormsModule,
    TranslocoModule,
    GroupMembersDirective,
  ],
  templateUrl: './fun-budget.component.html',
  styleUrl: './fun-budget.component.scss',
})
export class FunBudgetComponent {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly userService = inject(UserService);
  private readonly groupService = inject(GroupService);
  readonly TransactionLogType = FunTransactionLogType;
  readonly transactionLogTypeTranslation = transactionLogTypeTranslation;

  readonly now = new Date();
  me$ = this.getMe$();
  groups$ = this.getGroups$();
  readonly yearControl = this.formBuilder.control<string | undefined>(undefined);
  transactionLogData$ = this.getTransactionLogData$();

  reloadMe(): void {
    this.me$ = this.getMe$();
  }

  reloadGroups(): void {
    this.groups$ = this.getGroups$();
  }

  reloadTransactionLogData(): void {
    this.transactionLogData$ = this.getTransactionLogData$();
  }

  trackGroup(index: number, group: Group): number {
    return group.id;
  }

  trackUser(index: number, user: User): number {
    return user.id;
  }

  trackYearLog(index: number, log: FunTransactionLogForYear): number {
    return log.year;
  }

  trackMonthLog(index: number, log: FunTransactionLogForMonth): string {
    return log.monthName;
  }

  trackLog(index: number, log: FunTransactionLogRecord): number {
    return log.id;
  }

  private getMe$(): Observable<User> {
    return this.userService.getMe().pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  private getGroups$(): Observable<Group[]> {
    return this.groupService.getMyGroups().pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  private getTransactionLogData$(): Observable<TransactionLogData> {
    const activeYears$ = this.userService.getMyActiveYears().pipe(
      tap((activeYears) => {
        if (!this.yearControl.value && activeYears.length > 0)
          this.yearControl.patchValue(`${activeYears[0]}`);
      }),
    );
    const transactionLogs$ = defer(() =>
      this.yearControl.valueChanges.pipe(startWith(this.yearControl.value)),
    ).pipe(
      filter(
        (year): year is string =>
          typeof year === 'string' &&
          year.trim() !== '' &&
          Number.isSafeInteger(Number(year)) &&
          Number(year) > 0,
      ),
      distinctUntilChanged(),
      switchMap((year) =>
        this.userService
          .getMyFunTransactionLog(Number(year))
          .pipe(map((log) => log.records.filter((record) => `${record.year}` === year))),
      ),
    );
    return combineLatest([activeYears$, transactionLogs$]).pipe(
      map(([activeYears, transactionLogs]) => ({ activeYears, transactionLogs })),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
